import SessionManager from "@/apollo/datasources/sessionManager";
import config from "@/lib/config";
import connectRedis from "@/lib/connectRedis";
import Cookies from "@/lib/cookies";
import dayjs from "@/lib/day";
import { AuthenticationError } from "apollo-server-errors";
import { createHash } from "crypto";
import jwt from "jsonwebtoken";

export default class UserToken {
  constructor(req, res) {
    this.req = req;
    this.res = res;
    this.cookies = new Cookies(req, res);
    this.redis = connectRedis(
      {
        host: process.env.REDIS_HOST || config.token.redis?.host,
        port: process.env.REDIS_PORT || config.token.redis?.port,
        keyPrefix: config.token.redis.prefix,
        db: config.token.redis?.db,
      },
      config.token?.redis?.cluster
    );
  }

  get cookieName() {
    return config.token.name;
  }

  getToken() {
    return this.cookies.get(this.cookieName);
  }

  get signatureName() {
    return config.token.signature;
  }

  getSignature() {
    return this.cookies.get(this.signatureName);
  }

  getSecret(signature = null) {
    if (!signature) {
      signature = this.getSignature();
    }
    return `${signature}${config.token.secret}`;
  }

  generateKey(id, signature) {
    const key = (() => {
      return `token:${id}:${signature}`;
    })();
    return createHash("sha256").update(key).digest("hex");
  }

  async setPayload(payload, signature) {
    const key = this.generateKey(payload.id, signature);
    await this.redis
      .multi()
      .set(key, JSON.stringify(payload))
      .expire(key, config.token.maxAge)
      .exec();
    return key;
  }

  async getPayload(key) {
    const json = await this.redis.get(key);
    return JSON.parse(json);
  }

  deletePayload(key) {
    return this.redis.del(key);
  }

  async sign(payload, signature) {
    if (!signature) {
      signature = this.getSignature();
    }
    const key = await this.setPayload(payload, signature);
    const secret = this.getSecret(signature);
    const token = jwt.sign({ key }, secret);
    const options = { maxAge: config.token.maxAge };
    this.cookies.set([
      { name: this.signatureName, value: signature, options },
      { name: this.cookieName, value: token, options },
    ]);
    return token;
  }

  async refreshToken() {
    try {
      const sessionManager = new SessionManager({
        req: this.req,
        res: this.res,
      });
      const ret = await sessionManager.tokenRefresh();
      // リフレッシュしたら古いトークンを明示的に削除したほうがいいかもしれないが、複数のリクエストが来ていた時にエラーするかもしれない
      // const delRet = await sessionManager.tokenDelete();
      // console.log("old token delete", delRet);
      return {
        accessToken: ret.access_token,
        refreshToken: ret.refresh_token,
        tokenExpiredAt: dayjs().add(ret.expires_in, "second").format(),
      };
    } catch (e) {
      console.log("tokenRefresh Error:", e);
      console.log(e?.extension);
      this.delete();
      throw new AuthenticationError("Unauthorized");
    }
  }

  async refresh(updatePayload = {}) {
    const token = await this.verify();
    if (!token) return null;
    delete token.iat;
    delete token.exp;
    delete token.nbf;
    delete token.jti;
    // アクセストークンのexpireが近かったらリフレッシュする
    let refreshPayload = {};
    if (
      // tokenが発行した時刻から15分以上経過していればリフレッシュする
      dayjs(token.payload.tokenExpiredAt).isBefore(dayjs().add(15, "minutes"))
    ) {
      refreshPayload = await this.refreshToken();
    }
    const newPayload = Object.assign(
      {},
      token.payload,
      updatePayload,
      refreshPayload
    );
    await this.sign(newPayload);
    return newPayload;
  }

  // LOGICAトークン失効判定
  // 返却値 トークン失効: false, トークン有効: true
  async checkExpiration() {
    const token = await this.verify();
    if (!token) return false;
    return dayjs(token.payload.tokenExpiredAt).isAfter(dayjs());
  }

  async verify() {
    // cookieからtokenを取得
    const token = this.getToken();
    if (!token) return false;
    // cookieからsignatureを取得
    const secret = this.getSecret();
    // tokenを検証
    const verifiedToken = jwt.verify(token, secret);
    // redisからpayloadを取得
    const payload = await this.getPayload(verifiedToken.key);
    if (!payload) return false;
    return { ...verifiedToken, payload };
  }

  async delete() {
    const token = this.getToken();
    if (!token) {
      console.log("userToken delete, token not found");
      return false;
    }
    try {
      const secret = this.getSecret();
      const verifiedToken = jwt.verify(token, secret);
      await this.deletePayload(verifiedToken.key);
    } catch (e) {
      console.log("token delete error", e);
    } finally {
      this.cookies.remove([
        this.cookieName,
        this.signatureName,
        [config.optionalTermsConfirmCookieName, { domain: null }],
      ]);
    }
  }

  async deleteAll() {
    const removeCookies = [
      [config.optionalTermsConfirmCookieName, { domain: null }],
    ];
    try {
      [true, false].forEach(async (val) => {
        const token = this.getToken();
        if (token) {
          const secret = this.getSecret();
          const verifiedToken = jwt.verify(token, secret);
          removeCookies.push(this.cookieName);
          removeCookies.push(this.signatureName);
          await this.deletePayload(verifiedToken.key);
        }
      });
    } catch (e) {
      console.log("token delete error", e);
    } finally {
      this.cookies.remove(removeCookies);
    }
  }
}
